// --------------------------------------------------------------
// Created On: 2022-01-24
// Author: Zachary Thomas
//
// Last Modified: 2026-10-13
// Modified By: Jonathon Hicke
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useEffect } from "react";
import Modal from "../../../Modal/Modal";
import ModalHeader from "../../../ModalHeader/ModalHeader";
import ModalBody from "../../../ModalBody/ModalBody";
import ModalFooter from "../../../ModalFooter/ModalFooter";
import Error from "../../../Error/Error";
import Spinner from "../../../Spinner/Spinner";
import NotificationMessageForm from "./NotificationMessageForm/NotificationMessageForm";
import apiRequest from "../../../../utilities/api/apiRequest";
import getApiError from "../../../../utilities/api/getApiError";
import useReduxSelector from "../../../../hooks/useReduxSelector";
import {
  API,
  MIN_NOTIFICATION_MESSAGE_LENGTH,
  MAX_NOTIFICATION_MESSAGE_LENGTH,
} from "../../../../constants/miscellaneous";
import styles from "./NotificationMessageModal.module.scss";

// Modal for sending notification messages.
export default function NotificationMessageModal(props: Props): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [notificationMessage, setNotificationMessage] = useState<string>("");
  const currentUser = useReduxSelector((state) => state.currentUser);

  // Clear all data when the modal is opened.
  useEffect(() => {
    if (props.showModal) {
      setNotificationMessage("");
    }
  }, [props.showModal]);

  // Validate the notification message settings.
  function notificationMessageIsValid(): boolean {
    if (
      notificationMessage.trim().length < MIN_NOTIFICATION_MESSAGE_LENGTH ||
      notificationMessage.trim().length > MAX_NOTIFICATION_MESSAGE_LENGTH
    ) {
      setErrorMessage(
        `The notification message must be between ${MIN_NOTIFICATION_MESSAGE_LENGTH} and ${MAX_NOTIFICATION_MESSAGE_LENGTH} characters long.`
      );
      return false;
    } else {
      return true;
    }
  }

  // Send a notification message.
  async function sendNotificationMessage(): Promise<void> {
    if (notificationMessageIsValid()) {
      const requestBody = {
        message: notificationMessage.trim(),
        urgency: "INFO",
        audience: "COMPANY",
        companyId: currentUser.companyId,
        usergroupId: null,
        userId: null,
      };

      setLoading(true);
      const [response] = (await apiRequest(`${API}/alertmessage`, "POST", requestBody)) as [Response, ResponseBody];
      setLoading(false);

      if (response.ok) {
        setErrorMessage("");
        props.onClose();
      } else {
        setErrorMessage(await getApiError(response, "Unable to send notification."));
      }
    }
  }

  // Exit the modal.
  function exitModal(): void {
    setErrorMessage("");
    props.onClose();
  }

  return (
    <div>
      <Spinner loading={loading} />

      <Modal
        show={props.showModal}
        onHide={() => exitModal()}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--super-modal-z-index)" }}
        animation
        centered
      >
        <ModalHeader>
          <h5 className="font-weight-bold">Send Notification</h5>
        </ModalHeader>

        <ModalBody>
          <p className="mx-2">Notifications are sent to all users in your company.</p>

          <NotificationMessageForm
            notificationMessage={notificationMessage}
            onChangeNotificationMessage={(notificationMessage) => setNotificationMessage(notificationMessage)}
          />

          {errorMessage.length > 0 && (
            <div className="row">
              <div className="col mt-4 mx-2">
                <Error message={errorMessage} />
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <button
            data-test="modal-send-notification-button"
            className={`${styles.button} btn btn-primary`}
            type="button"
            onClick={() => sendNotificationMessage()}
          >
            Send Notification
          </button>

          <button className={`${styles.button} btn btn-secondary`} type="button" onClick={() => exitModal()}>
            Cancel
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

interface Props {
  showModal: boolean;
  onClose: () => void;
}

interface ResponseBody {
  message: string;
}
